import { Card, CardContent } from "@/components/ui/card";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import { getStatusStyle } from "@/lib/status-styles";
import { cn } from "@/lib/utils";

/**
 * KPI card — soft tinted fill with matching border (downtime mock).
 * @param {{
 *   title?: string,
 *   value?: string|number,
 *   subtitle?: string,
 *   icon?: import("lucide-react").LucideIcon,
 *   trend?: { isPositive: boolean, value: number },
 *   status?: string,
 *   additionalProperty?: { label: string, value: string|number },
 *   secondaryTitle?: string,
 *   secondaryValue?: string|number,
 *   isLoading?: boolean,
 * }} props
 */
export function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  status,
  additionalProperty,
  secondaryTitle,
  secondaryValue,
  isLoading = false,
}) {
  const statusStyle = getStatusStyle(status || "summary");

  return (
    <Card
      className={cn(
        "min-w-0 rounded-md shadow-sm transition-all duration-200",
        statusStyle.border,
        statusStyle.inactiveBg
      )}
      data-testid={`card-stat-${(title || "unknown").toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className='p-3'>
        <div className='flex min-w-0 items-start justify-between gap-2'>
          <div className='min-w-0 flex-1'>
            <div className='mb-2 flex min-w-0 gap-6'>
              <div className='min-w-0 flex-1'>
                <p
                  className={cn(
                    "mb-1 break-words text-xs font-semibold uppercase tracking-wide",
                    statusStyle.labelColor
                  )}>
                  {title || "Unknown"}
                </p>
                {isLoading ? (
                  <LoadingSkeleton
                    className='mt-1 h-7 w-16 rounded'
                    data-testid='text-stat-value-loading'
                  />
                ) : (
                  <p
                    className={cn(
                      "text-xl font-semibold text-foreground",
                      statusStyle.valueColor
                    )}
                    data-testid='text-stat-value'>
                    {value || "0"}
                  </p>
                )}
              </div>

              {secondaryTitle && secondaryValue && (
                <div className='min-w-0 flex-1'>
                  <p
                    className={cn(
                      "mb-1 break-words text-xs font-semibold uppercase tracking-wide",
                      statusStyle.labelColor
                    )}>
                    {secondaryTitle}
                  </p>
                  <p
                    className={cn(
                      "text-xl font-semibold text-foreground",
                      statusStyle.valueColor
                    )}
                    data-testid='text-secondary-stat-value'>
                    {secondaryValue}
                  </p>
                </div>
              )}
            </div>

            <div className='space-y-0.5'>
              {subtitle && (
                <p className={cn("text-xs font-medium", statusStyle.labelColor)}>
                  {subtitle}
                </p>
              )}
              {additionalProperty && (
                <div className='mt-1 flex items-center justify-between rounded bg-black/5 dark:bg-white/5 px-2 py-1'>
                  <span
                    className={cn(
                      "text-xs font-semibold",
                      statusStyle.labelColor
                    )}>
                    {additionalProperty.label}
                  </span>
                  <span className='text-xs font-semibold text-foreground'>
                    {additionalProperty.value}
                  </span>
                </div>
              )}
            </div>
          </div>
          {Icon && (
            <Icon
              className={cn("shrink-0 opacity-80", statusStyle.iconColor)}
              size={20}
              aria-hidden
            />
          )}
        </div>
        {trend && (
          <div className='mt-3 flex items-center gap-1.5 rounded bg-black/5 dark:bg-white/5 px-2 py-1'>
            <span
              className={cn(
                "text-xs font-semibold",
                trend.isPositive ? "text-green-600" : "text-red-600"
              )}>
              {trend.isPositive ? "↗" : "↘"} {Math.abs(trend.value)}%
            </span>
            <span className='text-xs text-muted-foreground'>vs. last week</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
